import { z } from "zod/v4";

// Define AccountIdentification1 schema
const AccountIdentification1Schema = z.object({
  type: z.string(),
  number: z.string(),
});

// Define Address2 schema
const Address2Schema = z.object({
  streetName: z.string().nullable(),
  buildingNumber: z.string().nullable(),
  postCode: z.string().nullable(),
  townName: z.string().nullable(),
  country: z.string().nullable(),
  department: z.string().nullable(),
  subDepartment: z.string().nullable(),
  addressType: z.string().nullable(),
});

// Define PayeeDetailsPerCurrency schema
const PayeeDetailsPerCurrencySchema = z.object({
  id: z.number(),
  selector: z.string(),
  forCurrency: z.string(),
  defaultForCurrency: z.boolean().nullable(),
  accountHolderName: z.string(),
  accountIdentification1: AccountIdentification1Schema,
  accountIdentification2: AccountIdentification1Schema.nullable(),
  payeeAddress: Address2Schema.nullable(),
  isVirtualAccount: z.boolean().nullable(),
  vaCustomerId: z.string().nullable(),
  vaAccountId: z.string().nullable(),
  vaDefaultPayOutBeneficiaryAccountId: z.string().nullable(),
  vaIsSandboxAccount: z.boolean().nullable(),
  // Read schema is tolerant of unmodeled provider values (staged backend
  // rollouts) so a single unknown value cannot crash the whole merchant
  // query. Unknown values degrade to "NONE"; virtual-ness is driven by
  // `isVirtualAccount`, not this field. Write schemas remain strict. See #651.
  vaProviderType: z
    .enum(["NONE", "MODULR", "OPTIMUS", "ISX", "DEVENGO", "CLEARBANK"])
    .catch("NONE"),
});

// Define Application schema
const ApplicationSchema = z.object({
  merchantId: z.string(),
  applicationId: z.string(),
  applicationName: z.string(),
  payeeDetailsPerCurrency: z.array(PayeeDetailsPerCurrencySchema).nullable(),
  merchantCallbackUrlForMobile: z.string().nullable(),
  merchantCallbackUrlForWeb: z.string().nullable(),
  paymentStatusWebhookUrl: z.string().nullable(),
  selectedYapilyClientId: z.string().nullable(),
  institutions: z.array(z.string()),
  hasVirtualAccountsEnabled: z.boolean(),
  refundWebhookUrl: z.string().nullable(),
  payoutWebhookUrl: z.string().nullable(),
  cancelUrl: z.string().nullable(),
  sendEmailForExternalPayinPayment: z.boolean().nullable(),
  sendWebhookForExternalPayinPayment: z.boolean().nullable(),
  confirmationOfPayerEnabled: z.boolean(),
  swiftPayoutsEnabled: z.boolean(),
});

// Define Merchant schema
export const MerchantSchema = z.object({
  merchantId: z.string(),
  email: z.string().nullable(),
  companyName: z.string(),
  paymentFlatFeeInPercentage: z.number(),
  applications: z.array(ApplicationSchema),
});

// Type definitions using the schemas
export type Merchant = z.infer<typeof MerchantSchema>;
export type Application = z.infer<typeof ApplicationSchema>;
export type PayeeDetailsPerCurrency = z.infer<
  typeof PayeeDetailsPerCurrencySchema
>;
